<template>
  <div class="dynamics-contact-form">
    <div class="dynamics-contact-form__header">
      <h4 class="h4">
        {{ tv('contactUs', 'contactForm') }}
      </h4>
      <p class="subheader">
        {{ tv('questions', 'contactForm') }}
      </p>
    </div>

    <!-- Topic Selection -->
    <div class="dynamics-contact-form__topic-selector">
      <label
        class="label"
        for="contactTopic"
      >
        {{ tv('selectTopic', 'dynamicsContactForm') }}*
      </label>
      <DropDown
        id="contactTopic"
        :data-items="topicOptions"
        text-field="label"
        data-item-key="id"
        :value="selectedTopic"
        :default-item="defaultTopicItem"
        @change="handleTopicChange"
      />
    </div>

    <!-- Loading State -->
    <div
      v-if="isLoadingForm"
      class="dynamics-contact-form__loading"
    >
      <AppLoadingIndicator />
    </div>

    <!-- Error State -->
    <div
      v-else-if="hasError"
      class="dynamics-contact-form__error"
    >
      <MessageContainer
        message="formLoadError"
        translation-file-name="dynamicsContactForm"
        :is-error="true"
      />
      <ReactiveButton
        :button-text="tv('retry', 'dynamicsContactForm')"
        :is-primary-button="true"
        @click="retryLoadForm"
      />
    </div>

    <!-- Dynamics Forms Container -->
    <div
      v-show="selectedTopic && !isLoadingForm && !hasError"
      ref="formHostContainer"
      class="dynamics-contact-form__forms-container"
    />
  </div>
</template>

<script setup lang="ts">
import {ref, computed, watch, nextTick, onMounted, onUnmounted} from 'vue'
import {useNuxtApp} from '#app'
import useI18nExtended from '~/composables/usei18nExtended'
import DropDown from '~/components/input-components/DropDown.vue'
import AppLoadingIndicator from '~/components/loading-indicators/app-loading-indicator.vue'
import MessageContainer from '~/components/MessageContainer.vue'
import ReactiveButton from '~/components/ReactiveButton.vue'

// Props
const props = defineProps<{
  prefilledMessage?: string
  initialTopicId?: string
  trackingEventCallback?: (() => void) | null
}>()

// Emits
const emit = defineEmits<{
  close: []
}>()

// Nuxt app instance for GTM and toast
const {$gtm, $toast} = useNuxtApp() as any

// i18n
const i18n = useI18nExtended()
const tv = i18n.tv

// Form Configuration Constants
const FORM_API_URL = 'https://public-eur.mkt.dynamics.com/api/v1.0/orgs/526b3040-ce06-41d6-8fcd-9101566ddae1/landingpageforms'
const FORM_ASSET_BASE_URL = 'https://assets-eur.mkt.dynamics.com/526b3040-ce06-41d6-8fcd-9101566ddae1/digitalassets/forms'
const FORM_LOADER_URL = 'https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/eur/FormLoader/FormLoader.bundle.js'

const FORM_IDS = {
  CONTACT_EN: 'bc6101b5-debb-f011-bbd2-000d3a66c45b',
  CONTACT_DE: '7697e2c3-1efd-f011-8406-000d3a67e77c',
  LEAD_CONTACT_EN: 'e05b09a9-debb-f011-bbd2-000d3a66c45b',
  LEAD_CONTACT_DE: '7bdf80a5-1efd-f011-8406-000d3a67e77c'
} as const

type FormType = 'contact' | 'lead-contact'

interface TopicOption {
  id: string
  label: string
  formType: FormType
}

// Topic definitions with form type mapping (labels are resolved via i18n below)
const TOPIC_DEFINITIONS: {id: string; labelKey: string; formType: FormType}[] = [
  {id: '2094787f1d2b44fd95247deded12fcea', labelKey: 'generalRequest', formType: 'contact'},
  {id: '7f0ccec806c643bb9ed75d8f32ec214d', labelKey: 'career', formType: 'contact'},
  {id: '873f0f53cd904e3598c93c6815457fdd', labelKey: 'pressMediaRelations', formType: 'contact'},
  {id: '1e53b8d6c40045a1b3eca1d70eb29350', labelKey: 'productRequest', formType: 'lead-contact'},
  {id: '9bc5a0f87eb64d11b6edac07c838bbf9', labelKey: 'freudenbergXpress', formType: 'lead-contact'},
  {id: 'd7ebd1fae2e0438a81310305849354cd', labelKey: 'dichtomatik', formType: 'lead-contact'},
  {id: '711bd51197f84740985c4fdfafcb2fa6', labelKey: 'noSuitableTopic', formType: 'contact'},
  {id: 'a9d83a58494a4d7da70edf08d1def26f', labelKey: 'supplierRelations', formType: 'contact'}
]

// State
const selectedTopic = ref<TopicOption | null>(null)
const isLoadingForm = ref(false)
const hasError = ref(false)
const formLoadTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const formObserver = ref<MutationObserver | null>(null)
const loadedFormId = ref<string>('')

// Track window keys added by FormLoader so we can clean them up
const windowKeysBeforeLoad = ref<Set<string> | null>(null)

// Refs
const formHostContainer = ref<HTMLElement | null>(null)

// Computed
const topicOptions = computed(() =>
  TOPIC_DEFINITIONS.map(t => ({
    id: t.id,
    label: tv(t.labelKey, 'contactTopics'),
    formType: t.formType
  }))
)

const defaultTopicItem = computed(() => ({
  id: '',
  label: i18n.tv('selectTopicPlaceholder', 'dynamicsContactForm') || 'Please select a topic...',
  formType: 'contact' as FormType
}))

// Auto-select topic from prop
if (props.initialTopicId) {
  const match = topicOptions.value.find(t => t.id === props.initialTopicId)
  if (match) {
    selectedTopic.value = match
  }
}

const currentLanguage = computed(() => {
  return i18n.locale.value === 'de' ? 'de' : 'en'
})

const currentFormType = computed<FormType>(() => {
  return selectedTopic.value?.formType || 'contact'
})

const currentFormId = computed(() => {
  if (!selectedTopic.value) {
    return ''
  }

  const lang = currentLanguage.value
  const type = currentFormType.value

  if (type === 'lead-contact') {
    return lang === 'de' ? FORM_IDS.LEAD_CONTACT_DE : FORM_IDS.LEAD_CONTACT_EN
  }
  return lang === 'de' ? FORM_IDS.CONTACT_DE : FORM_IDS.CONTACT_EN
})

const currentCachedFormUrl = computed(() => {
  if (!currentFormId.value) {
    return ''
  }
  return `${FORM_ASSET_BASE_URL}/${currentFormId.value}`
})

// Methods
const handleTopicChange = (event: {value: TopicOption}) => {
  if (event.value?.id) {
    selectedTopic.value = event.value
  }
}

/**
 * Remove all traces of FormLoader from window and DOM so it can
 * be re-injected and will run its initialization from scratch.
 */
const cleanupFormLoader = () => {
  // 1. Remove FormLoader script tags
  document.querySelectorAll('script[src*="FormLoader"]').forEach(el => el.remove())

  // 2. Remove any stylesheets/styles injected by FormLoader
  document.querySelectorAll('link[href*="FormLoader"], style[data-form-id]').forEach(el => el.remove())

  // 3. Delete all window globals that FormLoader added
  if (windowKeysBeforeLoad.value) {
    const currentKeys = Object.getOwnPropertyNames(window)
    for (const key of currentKeys) {
      if (!windowKeysBeforeLoad.value.has(key)) {
        try {
          delete (window as any)[key]
        } catch {
          // Some browser-internal properties can't be deleted
        }
      }
    }
  }

  // 4. Clear host container
  if (formHostContainer.value) {
    formHostContainer.value.innerHTML = ''
  }

  loadedFormId.value = ''
}

const loadFormLoaderScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Snapshot window keys before first load
    if (!windowKeysBeforeLoad.value) {
      windowKeysBeforeLoad.value = new Set(Object.getOwnPropertyNames(window))
    }

    const script = document.createElement('script')
    script.src = `${FORM_LOADER_URL}?t=${Date.now()}`
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load FormLoader script'))
    document.head.appendChild(script)
  })
}

const setupFormLoadObserver = (formDiv: HTMLElement) => {
  if (formObserver.value) {
    formObserver.value.disconnect()
  }

  formObserver.value = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length > 0) {
        const hasFormElements = formDiv.querySelector('form, input, select, textarea')
        if (hasFormElements) {
          isLoadingForm.value = false
          loadedFormId.value = formDiv.getAttribute('data-form-id') || ''
          if (formLoadTimeout.value) {
            clearTimeout(formLoadTimeout.value)
            formLoadTimeout.value = null
          }

          setupFormSubmissionTracking(formDiv)
          setAdditionalInfoField(formDiv)
          prefillDescriptionField(formDiv)
          formDiv.querySelectorAll('.drop-placeholder').forEach(el => el.remove())

          formObserver.value?.disconnect()
          break
        }
      }
    }
  })

  formObserver.value.observe(formDiv, {
    childList: true,
    subtree: true
  })
}

const initializeForm = async () => {
  if (!selectedTopic.value) {
    return
  }

  const formId = currentFormId.value
  const cachedFormUrl = currentCachedFormUrl.value

  if (!formId || !formHostContainer.value) {
    return
  }

  // Skip reload if the same form is already loaded — just update the prefilled field
  if (formId === loadedFormId.value) {
    const formDiv = formHostContainer.value.querySelector('[data-form-id]') as HTMLElement
    if (formDiv) {
      setAdditionalInfoField(formDiv)
    }
    return
  }

  // Cleanup previous state
  if (formLoadTimeout.value) {
    clearTimeout(formLoadTimeout.value)
    formLoadTimeout.value = null
  }
  if (formObserver.value) {
    formObserver.value.disconnect()
  }

  isLoadingForm.value = true
  hasError.value = false

  // Wipe all FormLoader state (globals, DOM, scripts)
  cleanupFormLoader()

  // Create a fresh container div with the Dynamics data attributes
  const formDiv = document.createElement('div')
  formDiv.setAttribute('data-form-id', formId)
  formDiv.setAttribute('data-form-api-url', FORM_API_URL)
  formDiv.setAttribute('data-cached-form-url', cachedFormUrl)
  formHostContainer.value.appendChild(formDiv)

  // Setup observer BEFORE loading the script
  setupFormLoadObserver(formDiv)

  // Timeout for form loading
  formLoadTimeout.value = setTimeout(() => {
    if (isLoadingForm.value) {
      hasError.value = true
      isLoadingForm.value = false
    }
  }, 15000)

  try {
    // Wait for next tick so DOM is committed
    await nextTick()
    // Load a fresh FormLoader — with globals wiped, it will re-initialize
    await loadFormLoaderScript()
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error initializing form:', error)
    hasError.value = true
    isLoadingForm.value = false
  }
}

const setupFormSubmissionTracking = (container: HTMLElement) => {
  const checkForSubmitButton = () => {
    const submitButton = container.querySelector('button[type="submit"], input[type="submit"], .submit-button')
    if (submitButton) {
      submitButton.addEventListener('click', handleFormSubmission)
      return true
    }
    return false
  }

  if (!checkForSubmitButton()) {
    const buttonObserver = new MutationObserver(() => {
      if (checkForSubmitButton()) {
        buttonObserver.disconnect()
      }
    })
    buttonObserver.observe(container, {childList: true, subtree: true})
  }

  const successObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      const addedNodes = Array.from(mutation.addedNodes)
      const hasSuccess = addedNodes.some(node => {
        if (node instanceof HTMLElement) {
          return node.classList.contains('success') ||
                 node.classList.contains('thank-you') ||
                 node.textContent?.toLowerCase().includes('thank you') ||
                 node.textContent?.toLowerCase().includes('danke')
        }
        return false
      })

      if (hasSuccess) {
        handleFormSubmissionSuccess()
        successObserver.disconnect()
      }
    }
  })

  successObserver.observe(container, {childList: true, subtree: true})
}

const handleFormSubmission = () => {
  $gtm?.trackEvent({
    event: 'contact',
    category: 'contact',
    action: 'send_contact_form',
    ecommerce: {contact_reason: selectedTopic.value?.label}
  })
}

const handleFormSubmissionSuccess = () => {
  if (props.trackingEventCallback && typeof props.trackingEventCallback === 'function') {
    props.trackingEventCallback()
  }

  $toast?.success(i18n.tv('success', 'contactForm'))

  setTimeout(() => {
    emit('close')
  }, 2000)
}

const setAdditionalInfoField = (container: HTMLElement) => {
  let additionalInfo = selectedTopic.value?.id || ''

  const selectors = [
    '[name="zz_additionalinformation"]',
    '[id*="zz_additionalinformation"]',
    'textarea[data-field="zz_additionalinformation"]',
    '[data-editorblocktype="TextAreaFormField"] textarea'
  ]

  const trySetField = () => {
    for (const selector of selectors) {
      const field = container.querySelector(selector) as HTMLInputElement | HTMLTextAreaElement
      if (field) {
        // Use native setter to bypass React's internal state management
        const isTextarea = field.tagName === 'TEXTAREA'
        const proto = isTextarea ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
        const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set
        if (nativeSetter) {
          nativeSetter.call(field, additionalInfo)
        } else {
          field.value = additionalInfo
        }
        field.dispatchEvent(new Event('input', {bubbles: true}))
        field.dispatchEvent(new Event('change', {bubbles: true}))
        return true
      }
    }
    return false
  }

  // Try immediately, retry via observer if not found yet
  if (!trySetField()) {
    const fieldObserver = new MutationObserver(() => {
      if (trySetField()) {
        fieldObserver.disconnect()
      }
    })
    fieldObserver.observe(container, {childList: true, subtree: true})

    // Also retry on a short delay — some Dynamics fields render after framework hydration
    setTimeout(() => trySetField(), 500)
    setTimeout(() => trySetField(), 1500)

    setTimeout(() => fieldObserver.disconnect(), 5000)
  }
}

const prefillDescriptionField = (container: HTMLElement) => {
  if (!props.prefilledMessage) {
    return
  }

  const trySetDescription = () => {
    const field = container.querySelector('[name="description"]') as HTMLTextAreaElement
    if (field) {
      const nativeSetter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set
      if (nativeSetter) {
        nativeSetter.call(field, props.prefilledMessage)
      } else {
        field.value = props.prefilledMessage!
      }
      field.dispatchEvent(new Event('input', {bubbles: true}))
      field.dispatchEvent(new Event('change', {bubbles: true}))
      return true
    }
    return false
  }

  if (!trySetDescription()) {
    const fieldObserver = new MutationObserver(() => {
      if (trySetDescription()) {
        fieldObserver.disconnect()
      }
    })
    fieldObserver.observe(container, {childList: true, subtree: true})
    setTimeout(() => trySetDescription(), 500)
    setTimeout(() => trySetDescription(), 1500)
    setTimeout(() => fieldObserver.disconnect(), 5000)
  }
}

const retryLoadForm = () => {
  hasError.value = false
  initializeForm()
}

// Watchers
watch(selectedTopic, (newTopic) => {
  if (newTopic) {
    initializeForm()
  }
})

watch(() => currentLanguage.value, () => {
  if (selectedTopic.value) {
    initializeForm()
  }
})

// Lifecycle
onMounted(() => {
  if (selectedTopic.value) {
    initializeForm()
  }
})

onUnmounted(() => {
  cleanupFormLoader()
  if (formLoadTimeout.value) {
    clearTimeout(formLoadTimeout.value)
  }
  if (formObserver.value) {
    formObserver.value.disconnect()
  }
})
</script>

<style lang="scss" scoped>
.dynamics-contact-form {
  &__header {
    margin-bottom: 1.5rem;

    .h4 {
      margin: 0 0 0.5rem;
      color: $color__text__default;
    }

    .subheader {
      margin: 0;
      color: $color__text__default;
      font-weight: $base-text-font-weight-light;
    }
  }

  &__topic-selector {
    margin-bottom: 1.5rem;

    .label {
      display: block;
      margin-bottom: 0.375rem;
      color: $color__text__default;
      font-weight: $base-text-font-weight-light;
      font-size: 1.1875rem;
    }

    :deep(.k-dropdownlist) {
      height: 2.8125rem;
    }
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;

    p {
      margin-top: 1rem;
      color: $color__text__default;
    }
  }

  &__error {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;

    .reactiveButton {
      align-self: center;
    }
  }

  &__forms-container {
    margin-top: 1rem;
    min-height: 25rem;

    :deep([data-layout]) {
      max-width: 100% !important;
    }

    :deep(.outer) {
      width: 100% !important;
    }

    :deep(.outer > tbody),
    :deep(.outer > tbody > tr),
    :deep(.outer > tbody > tr > th) {
      display: block !important;
      width: 100% !important;
      box-sizing: border-box;
    }

    :deep(.contactClassFields .multi tbody),
    :deep(.contactClassFields .multi tbody tr),
    :deep(.contactClassFields .multi tbody tr th),
    :deep(.yesFieldAcceptClassMain .multi tbody),
    :deep(.yesFieldAcceptClassMain .multi tbody tr),
    :deep(.yesFieldAcceptClassMain .multi tbody tr th) {
      display: block !important;
      width: 100% !important;
      height: auto !important;
      min-height: 0 !important;
      box-sizing: border-box;
    }

    :deep(.sendButtonClass) {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    :deep(.mandatoryClass) {
      padding: 0 !important;
    }

    :deep(.thFormClass) {
      padding-left: 0 !important;
      padding-right: 0 !important;
      background-color: $color__white !important;
    }

    :deep([data-section]) {
      background-color: $color__white !important;
    }

    :deep(.outer),
    :deep(.outer th),
    :deep(.outer td),
    :deep(.columnContainer),
    :deep(.inner) {
      background-color: $color__white !important;
    }

    :deep(.columnContainer) {
      padding: 0 !important;

      & + .columnContainer {
        margin-top: 0.75rem !important;
      }
    }

    :deep(.multi tbody tr th.innerFormsClass) {
      display: grid !important;
      grid-template-columns: 1fr 1fr;
      gap: 0.5rem;

      @include breakpoint-down($sm) {
        grid-template-columns: 1fr;
      }
    }

    :deep(.innerFormsClass > [data-editorblocktype="TextAreaFormField"]),
    :deep(.innerFormsClass > .lookupFormFieldBlock) {
      grid-column: 1 / -1;
    }

    :deep(.consentBlock) {
      padding: 1rem 0 0 !important;
    }

    :deep(.submitButton) {
      color: $color__text__default !important;
    }

    :deep(.dropPlaceholder) {
      display: none !important;
    }

    :deep(.phoneFormFieldBlock > .phoneCountryCode) {
      border: none !important;
    }

    :deep(input),
    :deep(textarea),
    :deep(select) {
      border: 0.0625rem solid $color--input__border !important;
    }
  }
}
</style>
